import { useEffect, useState } from 'react';
import { Select, TextField } from '@nyce/admin-kit/ui';
import { authClientFor, loadTargets, useTargets } from './session';
import type { Tenant } from './api';

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Every tenant of one AUTH environment (pages of 200, at most 2 000), by name. */
export async function allTenants(target: string): Promise<Tenant[]> {
  const out: Tenant[] = [];
  for (let page = 1; page <= 10; page++) {
    const r = await authClientFor(target).tenants.list({ page, pageSize: 200 });
    out.push(...r.items);
    if (out.length >= r.total || !r.items.length) break;
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

export const tenantLabel = (t: Tenant) =>
  `${t.name}${t.customerName ? ` (${t.customerName})` : ''}${t.status !== 'Active' ? ` – ${t.status.toLowerCase()}` : ''}`;

/**
 * An AUTH tenant of `target`, picked by name (type to filter); the value is its id. Without a session on that
 * environment it falls back to typing the id. A stored id that is not in the list stays visible as unknown.
 */
export function TenantPicker({
  target,
  label = 'AUTH tenant',
  value,
  onChange,
  hint,
  error,
  allowNone = true,
  exclude,
  onTenant,
}: {
  target: string | null | undefined;
  label?: string;
  value: string;
  onChange: (id: string) => void;
  hint?: string;
  error?: string;
  allowNone?: boolean;
  /** Ids not offered (e.g. tenants a service already registered). */
  exclude?: readonly string[];
  /** The picked tenant itself (null when typed or none), for defaults derived from it. */
  onTenant?: (tenant: Tenant | null) => void;
}) {
  const { signedIn } = useTargets();
  const [tenants, setTenants] = useState<Tenant[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const live = !!target && signedIn(target);
  useEffect(() => {
    if (!target || !live) return;
    let on = true;
    void loadTargets()
      .then(() => allTenants(target))
      .then((l) => on && setTenants(l))
      .catch((e) => on && setFailed((e as Error).message));
    return () => {
      on = false;
    };
  }, [target, live]);

  if (!live || failed)
    return (
      <TextField
        label={label}
        className="nyk-mono"
        value={value}
        onChange={(e) => onChange(e.target.value.trim())}
        placeholder="tenant id (GUID)"
        error={error ?? (value && !GUID.test(value) ? 'an AUTH tenant id (GUID)' : undefined)}
        hint={
          failed
            ? `Tenants could not be listed (${failed}); type the id.`
            : `Sign in to AUTH ${target ?? ''} (Identity) to pick by name.${hint ? ` ${hint}` : ''}`
        }
      />
    );
  const options = [
    ...(allowNone ? [{ value: '', label: '— none —' }] : []),
    ...(tenants ?? [])
      .filter((t) => t.id === value || !exclude?.includes(t.id))
      .map((t) => ({ value: t.id, label: tenantLabel(t) })),
  ];
  if (value && tenants && !tenants.some((t) => t.id === value))
    options.push({ value, label: `${value} (unknown tenant)` });
  return (
    <Select
      label={label}
      value={value}
      onChange={(id) => {
        onChange(id);
        onTenant?.(tenants?.find((t) => t.id === id) ?? null);
      }}
      options={options}
      searchable
      placeholder={tenants ? 'Type to search' : 'Loading tenants…'}
      hint={error ?? hint}
    />
  );
}
